import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { stringify } from "qs";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { HotelInventory } from "admin/types/hotelInventory";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { addDays, format } from "date-fns";
import { Hotel } from "admin/types/hotel";

const key = "hotelInventory";
const adapter = createEntityAdapter<HotelInventory.Inventory>();

const initialStateValues: HotelInventory.HotelInventoryState = {
  hotelInventory: null,
  fromDate: format(new Date(), "yyyy-MM-dd"),
  errors: null,
  loading: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchHotelInventories = createAsyncThunk<
  {
    hotelInventories: HotelInventory.Inventory[];
    fromDateString: string;
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    fromDate: Date;
    span: number;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelInventories`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      fromDate,
      span,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const toDate = addDays(fromDate, span - 1);
    const fromDateString = format(fromDate, "yyyy-MM-dd");
    const toDateString = format(toDate, "yyyy-MM-dd");
    const param = stringify(
      { from_date: fromDateString, to_date: toDateString },
      {
        arrayFormat: "repeat",
      },
    );
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/inventories.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/inventories.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      hotelInventories: result.data.hotelInventories,
      fromDateString: fromDateString,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelInventory = createAsyncThunk<
  { hotelInventory: HotelInventory.Inventory },
  {
    context: PageContext.PageContext;
    hotelId: string;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelInventory`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/inventories/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/inventories/${id}.json`;
    const result = await createWrapper().put(url, { inventory: params });
    return { hotelInventory: result.data.hotelInventory };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const updateHotelInventories = createAsyncThunk<
  { hotel: Hotel.Hotel },
  {
    context: PageContext.PageContext;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/updateHotelInventories`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/inventories.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/inventories.json`;
    const result = await createWrapper().post(url, { inventory: params });
    return { hotel: result.data.hotel };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelInventorySlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setFromDate: (state, action) => {
      state.fromDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelInventory };
      })
      .addCase(fetchHotelInventories.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.hotelInventories);
        state.fromDate = action.payload.fromDateString;
      })
      .addCase(putHotelInventory.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.hotelInventory);
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelInventoryByIdSelector,
  selectIds: hotelInventoryIdsSelector,
  selectEntities: hotelInventoryEntitiesSelector,
  selectAll: hotelInventoriesSelector,
  selectTotal: totalHotelInventoriesSelector,
} = adapter.getSelectors((state: RootState) => state.hotelInventory);

const stateSelector = (state: RootState) => state[key];

export const fromDateSelector = createSelector(
  stateSelector,
  (state) => state.fromDate,
);

export const hotelInventoriesByRoomIdSelector = createSelector(
  [hotelInventoriesSelector, (state, hotelRoomId: number) => hotelRoomId],
  (hotelInventories, hotelRoomId): HotelInventory.DateBasedInventory => {
    const filteredInventories = hotelInventories.filter(
      (hotelInventory) => hotelInventory.hotelRoomId === hotelRoomId,
    );
    const dateBasedInventories = {};
    filteredInventories.map((inventory) => {
      dateBasedInventories[inventory.date] = inventory;
    });

    return dateBasedInventories;
  },
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

/** action export **/
export const { setFromDate } = hotelInventorySlice.actions;
